import * as React from "react";
import { Label, type LabelSize } from "../atoms/label.js";
import { cx } from "../internal/cx.js";

export type InputOTPSize = "sm" | "md" | "lg" | "xl";
export type InputOTPMode = "numeric" | "alphanumeric";

export interface InputOTPProps {
  autoFocus?: boolean;
  className?: string;
  defaultValue?: string;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  id?: string;
  /** Maximum number of characters. Common: 4 (PIN), 6 (verification code). Default 6. */
  length?: number;
  label?: React.ReactNode;
  /** Allowed character class. `numeric` digits only, `alphanumeric` digits + letters (uppercased). */
  mode?: InputOTPMode;
  /** Override the input name. Useful for native form submissions. */
  name?: string;
  onComplete?: (value: string) => void;
  onValueChange?: (value: string) => void;
  optional?: boolean;
  /** Override the placeholder. Defaults to dashes that match the length (e.g. ──────). */
  placeholder?: string;
  size?: InputOTPSize;
  value?: string;
}

function getLabelSize(size: InputOTPSize): LabelSize {
  return size === "sm" ? "sm" : size === "xl" ? "lg" : "md";
}

const NUMERIC_RE = /[^0-9]/g;
const ALPHANUMERIC_RE = /[^0-9a-zA-Z]/g;

function sanitize(input: string, mode: InputOTPMode, max: number): string {
  const stripped = input.replace(mode === "numeric" ? NUMERIC_RE : ALPHANUMERIC_RE, "");
  const cased = mode === "alphanumeric" ? stripped.toUpperCase() : stripped;
  return cased.slice(0, max);
}

function useControllableValue(
  controlled: string | undefined,
  defaultValue: string | undefined,
  sanitizer: (value: string) => string,
  onChange?: (value: string) => void,
) {
  const isControlled = controlled !== undefined;
  const [internal, setInternal] = React.useState<string>(() => sanitizer(defaultValue ?? ""));
  const value = isControlled ? sanitizer(controlled) : internal;
  const setValue = React.useCallback(
    (next: string) => {
      const sanitized = sanitizer(next);
      if (!isControlled) setInternal(sanitized);
      onChange?.(sanitized);
    },
    [isControlled, onChange, sanitizer],
  );
  return [value, setValue] as const;
}

/**
 * Single-field one-time-code input.
 *
 * Renders one native `<input>` with `autocomplete="one-time-code"` and the
 * right `inputmode` for the mode, so that iOS Safari and Android Chrome
 * surface SMS code suggestions in the keyboard bar. Visually styled with
 * tabular-nums + letter-spacing for an OTP feel, but it is structurally a
 * regular text field that consumers can paste into, autofill, or type into.
 */
export function InputOTP({
  autoFocus,
  className,
  defaultValue,
  disabled,
  error,
  helperText,
  id,
  length = 6,
  label,
  mode = "numeric",
  name,
  onComplete,
  onValueChange,
  optional = false,
  placeholder,
  size = "md",
  value,
}: InputOTPProps) {
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const helperId = helperText && !error ? `${inputId}-helper` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = errorId ?? helperId;

  const sanitizer = React.useCallback(
    (next: string) => sanitize(next, mode, length),
    [length, mode],
  );
  const [otpValue, setOtpValue] = useControllableValue(
    value,
    defaultValue,
    sanitizer,
    onValueChange,
  );

  const lastReportedComplete = React.useRef<string>("");

  // Fire onComplete exactly once whenever the value reaches full length.
  React.useEffect(() => {
    if (otpValue.length === length && otpValue !== lastReportedComplete.current) {
      lastReportedComplete.current = otpValue;
      onComplete?.(otpValue);
    } else if (otpValue.length < length) {
      lastReportedComplete.current = "";
    }
  }, [otpValue, length, onComplete]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOtpValue(event.target.value);
  };

  const inputMode: React.HTMLAttributes<HTMLElement>["inputMode"] =
    mode === "numeric" ? "numeric" : "text";
  const filled = otpValue.length > 0;
  // The placeholder defaults to dashes equal to the field length, which gives
  // the user a visual cue of how many characters are expected without taking
  // any extra DOM (it disappears as soon as they start typing).
  const placeholderText = placeholder ?? "\u2013".repeat(length);

  return (
    <div className="cw-v2-input-field">
      {label ? (
        <Label htmlFor={inputId} optional={optional} size={getLabelSize(size)}>
          {label}
        </Label>
      ) : null}

      <div
        className={cx(
          "cw-v2-input",
          `cw-v2-input--${size}`,
          "cw-v2-input-otp",
          `cw-v2-input-otp--${size}`,
          error && "cw-v2-input--error",
          disabled && "cw-v2-input--disabled",
          filled && "cw-v2-input--filled",
          className,
        )}
      >
        <input
          id={inputId}
          name={name}
          type="text"
          inputMode={inputMode}
          pattern={mode === "numeric" ? "[0-9]*" : undefined}
          autoComplete="one-time-code"
          autoCapitalize={mode === "alphanumeric" ? "characters" : "none"}
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="done"
          maxLength={length}
          value={otpValue}
          onChange={handleChange}
          // biome-ignore lint/a11y/noAutofocus: OTP screens are dedicated to one task — autofocus on the code field is the canonical UX (consumer opts in via the autoFocus prop).
          autoFocus={autoFocus}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          placeholder={placeholderText}
          className="cw-v2-input__control cw-v2-input-otp__control"
        />
      </div>

      {error ? (
        <p className="cw-v2-input__message cw-v2-input__message--error" id={errorId}>
          {error}
        </p>
      ) : helperText ? (
        <p className="cw-v2-input__message" id={helperId}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
}

InputOTP.displayName = "InputOTP";
